

// FunCursor.js
// Hover anything with `data-fun-image="/path/to.jpg"` (cards, a project page) to show an image that follows the cursor.
// Moving between cards swaps the image with a flip transition.

// Shown over a card with no image of its own inside something that has one
// (a related card on a project page): the image flips away to nothing.
const BLANK = '#blank';

export default class FunCursor {
  constructor(opts = {}) {
    this.attr = opts.attr || 'data-fun-image';

    this.size = Number.isFinite(opts.size) ? opts.size : 100; // px
    this.offsetX = Number.isFinite(opts.offsetX) ? opts.offsetX : 18;
    this.offsetY = Number.isFinite(opts.offsetY) ? opts.offsetY : 22;

    // Above the project overlay (.cardfocus, 9999), which is added to the page after us
    this.zIndex = Number.isFinite(opts.zIndex) ? opts.zIndex : 10000;
    this.flipMs = Number.isFinite(opts.flipMs) ? opts.flipMs : 360;
    this.showMs = Number.isFinite(opts.showMs) ? opts.showMs : 220;
    this.hideMs = Number.isFinite(opts.hideMs) ? opts.hideMs : 180;
    // How long the pointer must sit in the gap after leaving a card before the
    // image around it (the project's) flips back
    this.returnMs = Number.isFinite(opts.returnMs) ? opts.returnMs : 250;

    // State
    this._enabled = false;
    this._mounted = false;
    this._raf = 0;

    // Display position (smoothed)
    this._dispX = -9999;
    this._dispY = -9999;
    // Target position (raw cursor + offset)
    this._targetX = 0;
    this._targetY = 0;

    this._currentCard = null;
    this._currentSrc = '';
    this._pendingSrc = '';
    this._isVisible = false;
    this._isFlipping = false;

    // Bind
    this._onPointerMove = this._onPointerMove.bind(this);
    this._onPointerDown = this._onPointerDown.bind(this);
    this._onPointerOut = this._onPointerOut.bind(this);
    this._tick = this._tick.bind(this);

    // If you prefer auto-init, pass { autoStart: true }
    if (opts.autoStart) this.start();
  }

  start() {
    if (this._enabled) return;
    this._enabled = true;

    if (!this._mounted) this._mount();

    // Pointer move drives both position and “which card am I on?” detection.
    window.addEventListener('pointermove', this._onPointerMove, { passive: true });
    // If the user taps/clicks, we still want the cursor to update right away.
    window.addEventListener('pointerdown', this._onPointerDown, { passive: true });
    // Leaving the window, or going into a video embed (which keeps its pointer
    // events to itself), would otherwise leave the image frozen at the edge.
    window.addEventListener('pointerout', this._onPointerOut, { passive: true });

    // A project page loaded directly: have its image ready for the first move
    const page = document.querySelector(`.cardfocus[${this.attr}]`);
    if (page) this.preload(page.getAttribute(this.attr));

    this._raf = requestAnimationFrame(this._tick);
  }

  stop() {
    if (!this._enabled) return;
    this._enabled = false;

    window.removeEventListener('pointermove', this._onPointerMove);
    window.removeEventListener('pointerdown', this._onPointerDown);
    window.removeEventListener('pointerout', this._onPointerOut);

    if (this._raf) cancelAnimationFrame(this._raf);
    this._raf = 0;

    this.hide(true);
  }

  destroy() {
    this.stop();
    if (this._root) this._root.remove();
    this._mounted = false;
  }

  /** Fetch an image ahead of time so it shows without a wait. */
  preload(src) {
    if (src && src !== BLANK) this._preload(src).catch(() => {});
  }

  show(src) {
    if (!src || src === BLANK) return;
    if (!this._mounted) this._mount();

    // First show sets the image immediately (no flip).
    if (!this._currentSrc) {
      this._setFrontSrc(src);
      this._currentSrc = src;
    }

    this._root.classList.add('is-visible');
    this._isVisible = true;
  }

  hide(immediate = false) {
    if (!this._mounted) return;

    this._root.classList.remove('is-visible');
    this._isVisible = false;
    this._currentCard = null;
    this._currentCardSrc = '';
    if (this._returnTimer) {
      clearTimeout(this._returnTimer);
      this._returnTimer = 0;
    }

    // Always reset flip state so nothing gets stuck
    this._root.classList.remove('is-flipped');
    this._flipped = false;
    this._isFlipping = false;
    this._pendingSrc = '';
    this._currentSrc = '';
    if (this._flipTimer) {
      clearTimeout(this._flipTimer);
      this._flipTimer = 0;
    }

    // Clear images so stale content doesn't flash on next show
    if (this._frontImg) this._frontImg.removeAttribute('src');
    if (this._backImg) this._backImg.removeAttribute('src');
  }

  /** Swap the image with a single flip — alternates front/back each time. */
  swapTo(src) {
    if (!src) return;
    if (!this._mounted) this._mount();

    // If not visible yet, just show. Nothing to show for a blank.
    if (!this._isVisible) {
      if (src === BLANK) return;
      this._setFrontSrc(src);
      this._currentSrc = src;
      this._flipped = false;
      this.show(src);
      return;
    }

    // No-op if already showing this.
    if (src === this._currentSrc) return;

    // If mid-flip, just update pending — it'll be picked up when the flip ends.
    this._pendingSrc = src;
    if (this._isFlipping) return;

    this._doFlip();
  }

  _doFlip() {
    const src = this._pendingSrc;
    if (!src || src === this._currentSrc) return;

    this._isFlipping = true;

    // Load the next face while hidden, then flip to reveal it.
    const willBeFlipped = !this._flipped;

    this._preload(src)
      .catch(() => {})
      .finally(() => {
        // Hidden while it loaded: hide() has already reset everything
        if (!this._isVisible) return;

        // Put the new image on the face that's currently hidden
        if (willBeFlipped) {
          this._setBackSrc(this._pendingSrc);
        } else {
          this._setFrontSrc(this._pendingSrc);
        }

        // Single flip to reveal
        this._flipped = willBeFlipped;
        this._root.classList.toggle('is-flipped', this._flipped);
        this._currentSrc = this._pendingSrc;

        // After transition ends, check if another swap queued
        this._flipTimer = window.setTimeout(() => {
          this._isFlipping = false;

          if (this._pendingSrc && this._pendingSrc !== this._currentSrc) {
            this._doFlip();
          }
        }, this.flipMs);
      });
  }

  /* =====================
     Internals
  ===================== */

  _mount() {
    if (this._mounted) return;
    this._mounted = true;

    // DOM
    this._root = document.createElement('div');
    this._root.className = 'fun-cursor';
    this._root.style.setProperty('--fun-size', `${this.size}px`);

    this._wrap = document.createElement('div');
    this._wrap.className = 'fun-cursor__wrap';

    this._stage = document.createElement('div');
    this._stage.className = 'fun-cursor__stage';

    this._card = document.createElement('div');
    this._card.className = 'fun-cursor__card';

    this._front = document.createElement('div');
    this._front.className = 'fun-cursor__face fun-cursor__face--front';
    this._frontImg = document.createElement('img');
    this._frontImg.className = 'fun-cursor__img fun-cursor__img--front';
    this._frontImg.alt = '';
    this._frontImg.decoding = 'async';
    this._frontImg.loading = 'eager';

    this._back = document.createElement('div');
    this._back.className = 'fun-cursor__face fun-cursor__face--back';
    this._backImg = document.createElement('img');
    this._backImg.className = 'fun-cursor__img fun-cursor__img--back';
    this._backImg.alt = '';
    this._backImg.decoding = 'async';
    this._backImg.loading = 'eager';

    this._front.appendChild(this._frontImg);
    this._back.appendChild(this._backImg);
    this._card.appendChild(this._front);
    this._card.appendChild(this._back);
    this._stage.appendChild(this._card);
    this._wrap.appendChild(this._stage);
    this._root.appendChild(this._wrap);

    document.body.appendChild(this._root);
  }

  _onPointerDown(e) {
    // Snap display position on click/tap so there's no lag on first interaction
    this._targetX = e.clientX + this.offsetX;
    this._targetY = e.clientY + this.offsetY;
    this._dispX = this._targetX;
    this._dispY = this._targetY;
  }

  _onPointerMove(e) {
    // Target position — display lerps toward this
    this._targetX = e.clientX + this.offsetX;
    this._targetY = e.clientY + this.offsetY;

    this._update(e.target);
  }

  _onPointerOut(e) {
    // No related target: the pointer left the page or went into an iframe
    if (!e.relatedTarget || e.relatedTarget.tagName === 'IFRAME') this.hide(false);
  }

  /**
   * The element under the pointer that decides the image, and that image.
   * The nearest element carrying an image wins, so a project page (which
   * carries its project's image) shows it everywhere except over related
   * cards, which show their own image — or nothing if they have none.
   */
  _resolve(target) {
    if (target?.tagName === 'IFRAME') return { el: target, src: '' };

    const el = target?.closest?.(`[${this.attr}], .card[data-entry]`) || null;
    let src = el?.getAttribute(this.attr) || '';
    if (el && !src && el.parentElement?.closest(`[${this.attr}]`)) src = BLANK;
    return { el, src };
  }

  _update(target) {
    // e.target already skips pointer-events:none (our overlay)
    const { el, src } = this._resolve(target);

    // Compare the image too: a project page swaps its image in place
    if (el === this._currentCard && src === this._currentCardSrc) return;

    const prev = this._currentCard;
    this._currentCard = el;
    this._currentCardSrc = src;

    if (this._returnTimer) {
      clearTimeout(this._returnTimer);
      this._returnTimer = 0;
    }

    const apply = () => {
      this._returnTimer = 0;
      if (src) this.swapTo(src);
      else this.hide(false);
    };

    // Stepping off a card into the gap around it (between related cards):
    // wait before flipping back to the project's image, so crossing to the
    // next card goes straight from one card's image to the next.
    if (src && prev && el && el !== prev && el.contains(prev) && this._isVisible) {
      this._returnTimer = window.setTimeout(apply, this.returnMs);
    } else {
      apply();
    }
  }

  _tick() {
    if (!this._enabled) return;

    // Smooth follow — tight lerp so it doesn't feel laggy
    const lerp = 0.45;
    this._dispX += (this._targetX - this._dispX) * lerp;
    this._dispY += (this._targetY - this._dispY) * lerp;

    // Clamp to viewport
    const pad = 10;
    const maxX = window.innerWidth - this.size - pad;
    const maxY = window.innerHeight - this.size - pad;

    const x = Math.max(pad, Math.min(this._dispX, maxX));
    const y = Math.max(pad, Math.min(this._dispY, maxY));

    if (this._root) {
      this._root.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
    }

    this._raf = requestAnimationFrame(this._tick);
  }

  _setFrontSrc(src) {
    this._setFace(this._front, this._frontImg, src);
  }

  _setBackSrc(src) {
    this._setFace(this._back, this._backImg, src);
  }

  // A blank face keeps its old image, just hidden, so the card keeps its height
  // for the flip (the front face sizes the card; the back is laid over it).
  _setFace(face, img, src) {
    if (!face || !img) return;
    const blank = src === BLANK;
    face.classList.toggle('is-blank', blank);
    if (!blank) img.src = src;
  }

  _preload(src) {
    if (src === BLANK) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('fun-cursor preload failed'));
      img.src = src;
    });
  }
}